import {
  type DashboardReportDto,
  datePl,
  type FinanceReportDto,
  type MarginGroupDto,
  type MarginReportDto,
  monthLabel,
  type SalesReportDto,
} from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Group, SimpleGrid, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconChartBar, IconInfoCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { CompanyBadgeById } from '../../components/Badges';
import { CompanyBadge } from '../../components/CompanyBadge';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { useCompanies } from '../../data';
import { HBars, MonthBars, moneyShort, pctText, StatTile } from './charts';
import { type Filters, toQuery } from './filters';

const SERIES_SIGNED = { key: 'signed', label: 'Umowy (brutto)', color: 'var(--chart-1)' };
const SERIES_PAID = { key: 'paid', label: 'Wpływy', color: 'var(--chart-2)' };

/** Sekcja zestawienia: wspólna karta sekcji z krótkim opisem nad treścią. */
function Section({ title, children, extra, description }: { title: string; children: ReactNode; extra?: ReactNode; description?: ReactNode }) {
  return (
    <SectionCard title={title} actions={extra}>
      {description && (
        <Text size="xs" c="dimmed" mb="sm">
          {description}
        </Text>
      )}
      {children}
    </SectionCard>
  );
}

const Empty = ({ children = 'Brak danych w wybranym okresie' }: { children?: string }) => <EmptyState compact icon={<IconChartBar size={18} />} title={children} />;

/**
 * Tabela z przewijaniem w poziomie na wąskim ekranie. Wyrównanie kolumn: `align` ('l' tekst, 'r' liczby i kwoty);
 * domyślnie pierwsza kolumna do lewej, pozostałe do prawej.
 */
function DataTable({ head, rows, minWidth = 520, align }: { head: ReactNode[]; rows: ReactNode[][]; minWidth?: number; align?: ('l' | 'r')[] }) {
  const right = (i: number) => (align ? align[i] === 'r' : i > 0);
  return (
    <Table.ScrollContainer minWidth={minWidth}>
      <Table verticalSpacing={6} striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {head.map((h, i) => (
              <Table.Th key={i} ta={right(i) ? 'right' : undefined}>
                {h}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => (
            <Table.Tr key={i}>
              {r.map((c, j) => (
                <Table.Td key={j} ta={right(j) ? 'right' : undefined} className={right(j) ? 'tabular' : undefined}>
                  {c}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

const prevHint = (label = 'poprzedniego okresu') => `względem ${label}`;

// ── pulpit ──────────────────────────────────────────────────────────────────────

export function DashboardSection({ f, onTab }: { f: Filters; onTab: (t: string) => void }) {
  const scope = { companyId: f.companyId, tradeCode: f.tradeCode, ownerId: f.ownerId };
  const q = useQuery({ queryKey: ['reports', 'dashboard', scope], queryFn: () => api<DashboardReportDto>(`/reports/dashboard?${toQuery(scope)}`) });
  const d = q.data;
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {d && (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: 6 }} spacing="sm">
            <StatTile label={`Umowy netto — ${d.month.label}`} value={<Money gr={d.month.signedNetGr} />} current={d.month.signedNetGr} previous={d.previousMonth.signedNetGr} hint={`${d.month.signedCount} umów; ${prevHint(`tego samego okresu ${d.previousMonth.label.split(' ')[0]}`)}`} />
            <StatTile label={`Wpływy — ${d.month.label}`} value={<Money gr={d.month.paidGr} />} current={d.month.paidGr} previous={d.previousMonth.paidGr} hint={prevHint(`tego samego okresu ${d.previousMonth.label.split(' ')[0]}`)} />
            <StatTile
              label="Należności po terminie"
              value={<Money gr={d.overdueGr} />}
              hint={`z ${moneyShort(d.openGr)} do zapłaty`}
              footer={
                d.overdueGr > 0 && (
                  <Anchor component="button" type="button" size="xs" mt={6} onClick={() => onTab('finanse')}>
                    Kto zalega →
                  </Anchor>
                )
              }
            />
            <StatTile label="Skuteczność (12 mies.)" value={pctText(d.conversionPct)} hint="zakresy z umową / rozstrzygnięte" />
            <StatTile label="Otwarte sprawy" value={d.openCases} hint="bez zamkniętych" />
            {d.margin && <StatTile label="Marża szacunkowa (12 mies.)" value={pctText(d.margin.marginPct)} hint={d.margin.missingCost ? `${d.margin.missingCost} poz. bez ceny zakupu` : 'z cen zakupu w ofertach'} />}
          </SimpleGrid>
          <Section title="Ostatnie 12 miesięcy" description="Wartość brutto podpisanych umów i aneksów oraz zarejestrowane wpłaty — po miesiącach.">
            <MonthBars
              label="Umowy i wpływy w ostatnich 12 miesiącach"
              series={[SERIES_SIGNED, SERIES_PAID]}
              data={d.trend.map((t) => ({ label: monthLabel(t.month), values: { signed: t.signedGrossGr, paid: t.paidGr } }))}
            />
          </Section>
        </Stack>
      )}
    </QueryState>
  );
}

// ── sprzedaż ────────────────────────────────────────────────────────────────────

export function SalesSection({ f, period }: { f: Filters; period: string }) {
  const q = useQuery({ queryKey: ['reports', 'sales', f], queryFn: () => api<SalesReportDto>(`/reports/sales?${toQuery(f)}`) });
  const r = q.data;
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {r && (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, xs: 2, lg: 5 }} spacing="sm">
            <StatTile label="Nowe sprawy" value={r.kpis.cases} current={r.kpis.cases} previous={r.previous.cases} hint={prevHint()} />
            <StatTile label="Wydane oferty" value={r.kpis.offersIssued} current={r.kpis.offersIssued} previous={r.previous.offersIssued} hint={prevHint()} />
            <StatTile label="Podpisane umowy" value={r.kpis.contractsSigned} current={r.kpis.contractsSigned} previous={r.previous.contractsSigned} hint={prevHint()} />
            <StatTile label="Wartość umów netto" value={<Money gr={r.kpis.signedNetGr} />} current={r.kpis.signedNetGr} previous={r.previous.signedNetGr} hint="z aneksami" />
            <StatTile label="Skuteczność" value={pctText(r.kpis.conversionPct)} current={r.kpis.conversionPct} previous={r.previous.conversionPct} unit="pkt" hint="umowa / (umowa + przegrana)" />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Section title="Lejek sprzedaży" description={`Zakresy prac (branża w sprawie) ze spraw założonych w okresie ${period} — do którego etapu doszły.`}>
              {r.funnel[0]!.scopes === 0 ? (
                <Empty />
              ) : (
                <>
                  <HBars
                    rows={r.funnel.map((s, i) => {
                      const prev = i > 0 ? r.funnel[i - 1]!.scopes : null;
                      return {
                        key: s.stage,
                        label: s.label,
                        value: s.scopes,
                        text: `${s.scopes} (${pctText(Math.round((s.scopes / r.funnel[0]!.scopes) * 1000) / 10)})`,
                        sub: prev !== null ? `${pctText(prev ? Math.round((s.scopes / prev) * 1000) / 10 : null)} z poprzedniego etapu · spraw: ${s.cases}` : `spraw: ${s.cases}`,
                      };
                    })}
                  />
                  <Group gap="lg" mt="md">
                    <Text size="sm">
                      Przegrane: <b>{r.lostScopes}</b>
                    </Text>
                    <Text size="sm">
                      W toku: <b>{r.openScopes}</b>
                    </Text>
                  </Group>
                </>
              )}
            </Section>

            <Section title="Źródła klientów" description="Sprawy z okresu wg źródła i wartość umów, które z nich powstały (także podpisanych później).">
              {r.sources.length === 0 ? (
                <Empty />
              ) : (
                <HBars
                  rows={r.sources.map((s) => ({
                    key: s.source,
                    label: s.label,
                    value: s.signedNetGr,
                    text: <Money gr={s.signedNetGr} />,
                    sub: `${s.cases} spraw · ${s.casesSigned} z umową · skuteczność ${pctText(s.conversionPct)}`,
                  }))}
                />
              )}
            </Section>
          </SimpleGrid>

          <Section title="Opiekunowie" description="Sprawy i skuteczność — ze spraw założonych w okresie; oferty, umowy i wartość — wydane / podpisane w okresie.">
            {r.owners.length === 0 ? (
              <Empty />
            ) : (
              <DataTable
                minWidth={760}
                head={['Opiekun', 'Nowe sprawy', 'Wydane oferty', 'Umowy', 'Wartość netto', 'Śr. dni do umowy', 'Skuteczność']}
                rows={r.owners.map((o) => [o.name, o.cases, o.offersIssued, o.contractsSigned, <Money key="v" gr={o.signedNetGr} />, o.avgDaysToSign ?? '—', pctText(o.conversionPct)])}
              />
            )}
          </Section>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Section title="Branże" description="Wartość netto umów i aneksów podpisanych w okresie, wg branży pozycji.">
              {r.trades.length === 0 ? (
                <Empty />
              ) : (
                <HBars
                  rows={r.trades.map((t) => ({
                    key: t.tradeCode,
                    label: t.name,
                    value: t.signedNetGr,
                    text: <Money gr={t.signedNetGr} />,
                    color: t.color,
                    sub: `${t.contracts} umów · średnio ${t.avgNetGr === null ? '—' : moneyShort(t.avgNetGr)}`,
                  }))}
                />
              )}
            </Section>
            <Section title="Dlaczego przegrywamy" description="Powody przegranych zakresów (spraw z okresu) i odrzuceń ofert przez klienta (w okresie).">
              {r.lostReasons.length === 0 && r.rejections.length === 0 ? (
                <Empty>Brak przegranych i odrzuconych ofert</Empty>
              ) : (
                <Stack gap="md">
                  {r.lostReasons.length > 0 && (
                    <div>
                      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
                        Zakresy przegrane
                      </Text>
                      <HBars color="var(--chart-2)" rows={r.lostReasons.map((l) => ({ key: l.reason, label: l.label, value: l.scopes, text: l.scopes }))} />
                    </div>
                  )}
                  {r.rejections.length > 0 && (
                    <div>
                      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
                        Oferty odrzucone przez klienta
                      </Text>
                      <HBars
                        color="var(--chart-2)"
                        rows={r.rejections.map((x) => ({ key: x.reason, label: x.label, value: x.offers, text: x.offers, sub: `wartość ofert ${moneyShort(x.valueNetGr)} netto (najwyższy wariant)` }))}
                      />
                    </div>
                  )}
                </Stack>
              )}
            </Section>
          </SimpleGrid>
        </Stack>
      )}
    </QueryState>
  );
}

// ── finanse ─────────────────────────────────────────────────────────────────────

export function FinanceSection({ f, period }: { f: Filters; period: string }) {
  const q = useQuery({ queryKey: ['reports', 'finance', f], queryFn: () => api<FinanceReportDto>(`/reports/finance?${toQuery(f)}`) });
  const r = q.data;
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {r && (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="sm">
            <StatTile label="Umowy netto" value={<Money gr={r.kpis.signedNetGr} />} current={r.kpis.signedNetGr} previous={r.previous.signedNetGr} hint={prevHint()} />
            <StatTile label="Umowy brutto" value={<Money gr={r.kpis.signedGrossGr} />} current={r.kpis.signedGrossGr} previous={r.previous.signedGrossGr} hint={prevHint()} />
            <StatTile label="Wpływy" value={<Money gr={r.kpis.paidGr} />} current={r.kpis.paidGr} previous={r.previous.paidGr} hint={prevHint()} />
            <StatTile
              label="Zafakturowano brutto"
              value={r.invoicing.connected ? <Money gr={r.kpis.invoicedGrossGr} /> : '—'}
              current={r.invoicing.connected ? r.kpis.invoicedGrossGr : null}
              previous={r.previous.invoicedGrossGr}
              hint={r.invoicing.connected ? `${r.invoicing.documents} dok.; opłacono ${moneyShort(r.invoicing.paidGrossGr)}` : 'Fakturownia niepodłączona'}
            />
          </SimpleGrid>

          <Section title="Umowy i wpływy po miesiącach" description={`Okres ${period}. Umowy i aneksy wg daty podpisu (brutto), wpłaty wg daty wpłaty.`}>
            <MonthBars label="Umowy i wpływy po miesiącach" series={[SERIES_SIGNED, SERIES_PAID]} data={r.months.map((m) => ({ label: monthLabel(m.month), values: { signed: m.signedGrossGr, paid: m.paidGr } }))} />
            <div style={{ marginTop: 12 }}>
              <DataTable
                minWidth={620}
                head={['Miesiąc', 'Umowy netto', 'Umowy brutto', 'Wpływy', 'Zafakturowano brutto']}
                rows={[
                  ...r.months.map((m) => [monthLabel(m.month, true), <Money key="a" gr={m.signedNetGr} />, <Money key="b" gr={m.signedGrossGr} />, <Money key="c" gr={m.paidGr} />, <Money key="d" gr={m.invoicedGrossGr} />]),
                  [
                    <b key="r">Razem</b>,
                    <b key="a">
                      <Money gr={r.kpis.signedNetGr} />
                    </b>,
                    <b key="b">
                      <Money gr={r.kpis.signedGrossGr} />
                    </b>,
                    <b key="c">
                      <Money gr={r.kpis.paidGr} />
                    </b>,
                    <b key="d">
                      <Money gr={r.kpis.invoicedGrossGr} />
                    </b>,
                  ],
                ]}
              />
            </div>
          </Section>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Section title="Firmy" description="Umowy netto i wpływy w okresie.">
              {r.byCompany.length === 0 ? (
                <Empty />
              ) : (
                <DataTable
                  minWidth={360}
                  head={['Firma', 'Umowy netto', 'Wpływy']}
                  rows={r.byCompany.map((c) => [
                    <Group key="f" gap={6} wrap="nowrap">
                      <CompanyBadgeById id={c.companyId} />
                      <Text span size="sm">
                        {c.name}
                      </Text>
                    </Group>,
                    <Money key="a" gr={c.signedNetGr} />,
                    <Money key="b" gr={c.paidGr} />,
                  ])}
                />
              )}
            </Section>
            <Section title="Branże" description="Umowy netto w okresie, wg branży pozycji.">
              {r.byTrade.length === 0 ? <Empty /> : <HBars rows={r.byTrade.map((t) => ({ key: t.tradeCode, label: t.name, value: t.signedNetGr, text: <Money gr={t.signedNetGr} />, color: t.color }))} />}
            </Section>
          </SimpleGrid>

          <Title order={4} mt="sm">
            Należności — stan na dziś
          </Title>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Section title="Po terminie" extra={<Text fw={700} className="tabular" c={r.receivables.overdueGr ? 'var(--app-text-red)' : undefined}><Money gr={r.receivables.overdueGr} /></Text>} description={`Do zapłaty razem: ${moneyShort(r.receivables.openGr)}.`}>
              {r.receivables.overdueGr === 0 ? (
                <Empty>Brak należności po terminie</Empty>
              ) : (
                <HBars color="var(--app-text-red)" rows={r.receivables.aging.map((a) => ({ key: a.bucket, label: a.label, value: a.amountGr, text: <Money gr={a.amountGr} />, sub: `${a.items} poz. harmonogramu` }))} />
              )}
            </Section>
            <Section title="Prognoza wpływów" description="Niezapłacone pozycje harmonogramów z terminem w kolejnych miesiącach (bez zaległych).">
              <HBars
                color="var(--chart-2)"
                rows={[
                  ...r.receivables.forecast.map((m) => ({ key: m.month, label: monthLabel(m.month, true), value: m.dueGr, text: <Money gr={m.dueGr} /> })),
                  { key: 'brak', label: 'bez terminu (np. po odbiorze)', value: r.receivables.noDateGr, text: <Money gr={r.receivables.noDateGr} /> },
                ]}
              />
            </Section>
          </SimpleGrid>
          <Section title="Najwięksi dłużnicy" extra={<Anchor component={Link} to="/finanse" size="sm">Należności →</Anchor>}>
            {r.receivables.debtors.length === 0 ? (
              <Empty>Brak otwartych należności</Empty>
            ) : (
              <DataTable
                minWidth={620}
                head={['Płatnik', 'Po terminie', 'Do zapłaty', 'Umowy', 'Najstarszy termin']}
                align={['l', 'r', 'r', 'r', 'l']}
                rows={r.receivables.debtors.map((d) => [
                  <Anchor key="n" component={Link} to={`/klienci/${d.partyId}`} size="sm">
                    {d.name}
                  </Anchor>,
                  d.overdueGr ? (
                    <Text key="o" span size="sm" c="var(--app-text-red)" fw={600}>
                      <Money gr={d.overdueGr} />
                    </Text>
                  ) : (
                    '—'
                  ),
                  <Money key="t" gr={d.openGr} />,
                  d.contracts,
                  d.oldestDueDate ? datePl(d.oldestDueDate) : '—',
                ])}
              />
            )}
          </Section>
          {r.invoicing.connected && (
            <Text size="sm" c="dimmed">
              Wpłaty bez faktury i nieopłacone faktury:{' '}
              <Anchor component={Link} to="/finanse?widok=faktury" size="sm">
                Finanse → Fakturowanie
              </Anchor>
            </Text>
          )}
        </Stack>
      )}
    </QueryState>
  );
}

// ── marża ───────────────────────────────────────────────────────────────────────

function MarginCell({ g }: { g: Pick<MarginGroupDto, 'marginPct' | 'missingCost'> }) {
  const color = g.marginPct === null ? undefined : g.marginPct < 15 ? 'var(--app-text-red)' : undefined;
  return (
    <Group gap={6} justify="flex-end" wrap="nowrap">
      <Text span size="sm" fw={600} c={color}>
        {pctText(g.marginPct)}
      </Text>
      {g.missingCost > 0 && (
        <Tooltip label={`${g.missingCost} poz. bez ceny zakupu — marża niepełna, liczona tylko z pozycji z kosztem`} withArrow>
          <Badge size="xs" variant="light" color="orange" style={{ cursor: 'help' }}>
            niepełna
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

export function MarginSection({ f, period }: { f: Filters; period: string }) {
  const q = useQuery({ queryKey: ['reports', 'margin', f], queryFn: () => api<MarginReportDto>(`/reports/margin?${toQuery(f)}`) });
  const r = q.data;
  const companies = useCompanies();
  const groupRows = (list: MarginGroupDto[]) => list.map((g) => [g.name, <Money key="n" gr={g.netGr} />, ...(r?.amounts ? [<Money key="c" gr={g.costGr} />, <Money key="m" gr={g.marginGr} />] : []), <MarginCell key="p" g={g} />]);
  const head = (first: string) => [first, 'Netto', ...(r?.amounts ? ['Koszt szacunkowy', 'Marża'] : []), 'Marża %'];
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {r && (
        <Stack gap="md">
          <Alert color="gray" variant="light" icon={<IconInfoCircle />}>
            Marża <b>szacunkowa</b>: koszt z cen zakupu w zaakceptowanych wariantach ofert, nie rzeczywisty koszt realizacji. Liczona tylko od zakresów z ceną zakupu — aneksy i pozycje bez kosztu jej nie podnoszą, a takie wiersze mają oznaczenie „niepełna”.
          </Alert>
          <SimpleGrid cols={{ base: 1, xs: 2, lg: r.amounts ? 4 : 2 }} spacing="sm">
            <StatTile label="Umowy netto" value={<Money gr={r.total.netGr} />} hint={period} />
            {r.amounts && <StatTile label="Koszt szacunkowy" value={<Money gr={r.total.costGr} />} />}
            {r.amounts && <StatTile label="Marża kwotowa" value={<Money gr={r.total.marginGr} />} />}
            <StatTile label="Marża %" value={pctText(r.total.marginPct)} hint={r.total.missingCost ? `${r.total.missingCost} poz. bez ceny zakupu` : 'wszystkie pozycje z ceną zakupu'} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Section title="Branże">{r.byTrade.length ? <DataTable minWidth={r.amounts ? 560 : 360} head={head('Branża')} rows={groupRows(r.byTrade)} /> : <Empty />}</Section>
            <Section title="Opiekunowie">{r.byOwner.length ? <DataTable minWidth={r.amounts ? 560 : 360} head={head('Opiekun')} rows={groupRows(r.byOwner)} /> : <Empty />}</Section>
          </SimpleGrid>
          <Section title="Umowy" description="Od najniższej marży. Kliknij numer, aby otworzyć umowę.">
            {r.contracts.length === 0 ? (
              <Empty />
            ) : (
              <DataTable
                minWidth={r.amounts ? 900 : 700}
                head={['Umowa', 'Klient', 'Opiekun', 'Podpisano', 'Netto', ...(r.amounts ? ['Koszt szacunkowy', 'Marża'] : []), 'Marża %']}
                align={['l', 'l', 'l', 'l', 'r', ...(r.amounts ? (['r', 'r'] as const) : []), 'r']}
                rows={r.contracts.map((c) => [
                  <div key="u">
                    <Anchor component={Link} to={`/umowy/${c.contractId}`} size="sm" fw={600}>
                      {c.number || 'bez numeru'}
                    </Anchor>
                    <Group gap={4} wrap="nowrap">
                      {(() => {
                        const co = companies.data?.find((x) => x.code === c.companyCode);
                        return co ? <CompanyBadge company={co} size="xs" /> : <Text span size="xs" c="dimmed">{c.companyCode}</Text>;
                      })()}
                      <Text span size="xs" c="dimmed">
                        {c.kind === 'aneks' ? 'aneks · ' : ''}
                        {c.caseNumber}
                      </Text>
                    </Group>
                  </div>,
                  c.client,
                  c.ownerName,
                  datePl(c.signedAt),
                  <Money key="n" gr={c.netGr} />,
                  ...(r.amounts ? [<Money key="c" gr={c.costGr} />, <Money key="m" gr={c.marginGr} />] : []),
                  <MarginCell key="p" g={c} />,
                ])}
              />
            )}
          </Section>
          <Group>
            <Button variant="subtle" size="xs" component={Link} to="/katalog">
              Uzupełnij ceny zakupu w katalogu
            </Button>
          </Group>
        </Stack>
      )}
    </QueryState>
  );
}
